import { getBackendUrl, resolveAssetUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  CheckCircle, 
  Users, 
  Clock, 
  Shield, 
  MessageSquare,
  BarChart3,
  Loader2,
  Menu,
  X,
  Globe
} from 'lucide-react';
import SeoHead from '../components/SeoHead';
import TalentExamplesSection from '../components/landing/TalentExamplesSection';
import TestimonialsSection from '../components/landing/TestimonialsSection';
import FAQSection from '../components/landing/FAQSection';
import ProcessSection from '../components/landing/ProcessSection';
import { talentExamples, talentTypes, processSteps, testimonials, stats, faqItems } from '../data/landingPageData';

const ClientHomePage = () => {
  const navigate = useNavigate();
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoDarkUrl, setLogoDarkUrl] = useState(null);
  const [platformName, setPlatformName] = useState('Talent Partner');
  const [formLoading, setFormLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    talent_types: [],
    message: ''
  });

  const toggleTalentType = (type) => {
    setFormData((prev) => {
      const exists = prev.talent_types.includes(type);
      return {
        ...prev,
        talent_types: exists
          ? prev.talent_types.filter((t) => t !== type)
          : [...prev.talent_types, type]
      };
    });
  };

  useEffect(() => {
    // Fetch branding
    fetch(getBackendUrl() + '/api/branding')
      .then(res => res.json())
      .then(data => {
        if (data.logo_light_url) {
          setLogoUrl(data.logo_light_url);
        }
        if (data.logo_dark_url) {
          setLogoDarkUrl(data.logo_dark_url);
        }
        if (data.platform_name) {
          setPlatformName(data.platform_name);
        }
      })
      .catch(err => console.error('Failed to fetch branding:', err));

    // Funnel: landing view
    import('../lib/funnel').then(({ trackFunnel }) => trackFunnel('landing_view', { source: '/' }));
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.company || !formData.email || formData.talent_types.length === 0) {
      toast.error('Veuillez remplir tous les champs obligatoires (dont au moins un type de profil)');
      return;
    }
    
    setFormLoading(true);
    
    try {
      const response = await fetch(getBackendUrl() + '/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          // Backend expects single string — join multiple profiles with ", "
          talent_type: formData.talent_types.join(', ')
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Redirection vers la vraie page de remerciement (qui fire la conversion gtag).
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          talent_types: [],
          message: ''
        });
        navigate('/merci', {
          state: {
            fromForm: 'homepage',
            name: formData.name || '',
          },
        });
      } else {
        toast.error(data.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Erreur lors de l\'envoi de votre demande');
    } finally {
      setFormLoading(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Page-specific JSON-LD: FAQ schema + catalogue of talent Services.
  // Injected IN ADDITION to the global Organization/EmploymentAgency
  // declared in index.html → helps Google rich snippets & LLM discovery.
  const homeJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((q) => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer,
          },
        })),
      },
      {
        '@type': 'OfferCatalog',
        name: 'Profils francophones disponibles',
        itemListElement: talentTypes.map((type) => ({
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: type,
            provider: { '@id': 'https://talent-partner-solutions.com/#agency' },
            serviceType: 'Placement de talent francophone à distance',
            areaServed: ['FR', 'BE', 'CH', 'CA'],
          },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* SEO Meta Tags + FAQ/Service schemas */}
      <SeoHead
        canonical="https://talent-partner-solutions.com/"
        jsonLd={homeJsonLd}
      />
      
      {/* Navigation - White with dark primary text */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-tp-border" data-testid="client-navigation">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <Link to="/" className="flex items-center flex-shrink-0" data-testid="client-logo-link">
              {logoUrl ? (
                <img 
                  src={resolveAssetUrl(logoUrl)}
                  alt={platformName}
                  className="h-10 sm:h-12 max-w-[180px] sm:max-w-[220px] object-contain"
                  data-testid="client-nav-logo"
                />
              ) : (
                <span className="text-base sm:text-xl font-display font-semibold text-tp-primary truncate max-w-[140px] sm:max-w-none">
                  {platformName}
                </span>
              )}
            </Link>
            
            <div className="flex items-center gap-2">
              {/* Desktop CTA */}
              <Button 
                onClick={scrollToContact}
                className="bg-tp-accent hover:bg-tp-accent-light text-white text-xs sm:text-sm px-3 sm:px-4 h-8 sm:h-10" 
                data-testid="client-nav-cta"
              >
                <span className="hidden sm:inline">Recevoir des profils</span>
                <span className="sm:hidden">Profils</span>
              </Button>
              
              {/* Mobile Menu Button */}
              <button
                className="sm:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Menu"
                data-testid="client-mobile-menu-toggle"
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5 text-gray-700" />
                ) : (
                  <Menu className="h-5 w-5 text-gray-700" />
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        <div 
          className={`sm:hidden overflow-hidden transition-all duration-300 ease-in-out border-t border-tp-border ${
            mobileMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
          }`}
          data-testid="client-mobile-menu"
        >
          <div className="px-4 py-3 space-y-1 bg-white">
            <a 
              href="#services" 
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Nos services
            </a>
            <a 
              href="#profiles" 
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Exemples de profils
            </a>
            <a 
              href="#testimonials" 
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Témoignages
            </a>
            <a 
              href="#faq" 
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              FAQ
            </a>
            <Link 
              to="/postuler"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-tp-accent hover:bg-tp-accent/5"
            >
              Je cherche un emploi
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section - Primary Blue Gradient */}
      <section 
        className="relative py-12 sm:py-16 md:py-28 overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #0F2744 0%, #152F57 100%)'
        }}
        data-testid="client-hero-section"
      >
        <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-white/10 text-white text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              <Globe className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="leading-tight">Talents recrutés et opérés depuis Madagascar</span>
            </div>
            
            <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-semibold tracking-tight text-white mb-4 sm:mb-6 leading-tight">
              Recrutez plus vite, <br/>
              <span className="text-tp-accent-light">à coût maîtrisé</span>
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/80 mb-6 sm:mb-8 leading-relaxed max-w-2xl">
              Accédez à des collaborateurs francophones qualifiés, rigoureusement sélectionnés et prêts à intégrer votre équipe en remote.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                onClick={scrollToContact}
                className="bg-tp-accent hover:bg-tp-accent-light text-white text-sm sm:text-base px-4 sm:px-8 h-10 sm:h-12 w-full sm:w-auto" 
                data-testid="hero-cta-primary"
              >
                Recevoir des profils <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <Link 
                to="/postuler"
                className="w-full sm:w-auto"
              >
                <Button variant="outline" className="text-sm sm:text-base px-4 sm:px-8 h-10 sm:h-12 gap-2 bg-white/10 border-white/30 text-white hover:bg-white/20 w-full" data-testid="hero-cta-apply">
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
                  Déposer ma candidature
                </Button>
              </Link>
            </div>
            
            {/* Trust indicators */}
            <div className="flex flex-col xs:flex-row flex-wrap items-start xs:items-center gap-3 sm:gap-6 mt-6 sm:mt-10 pt-6 sm:pt-8 border-t border-white/20">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-white/70">
                <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-tp-accent-light flex-shrink-0" />
                <span>Profils validés sous 72h</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-white/70">
                <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-tp-accent-light flex-shrink-0" />
                <span>Contrats en règle</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-white/70">
                <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-tp-accent-light flex-shrink-0" />
                <span>Support continu</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof - Stats - Primary Light */}
      <section className="py-8 sm:py-12 bg-tp-primary-light" data-testid="stats-section">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={`stat-${stat.label}`} className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-white mb-0.5 sm:mb-1">
                  {stat.value}
                </div>
                <div className="text-xs sm:text-sm text-white/70">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TalentExamplesSection talents={talentExamples} onContactClick={scrollToContact} />

      {/* Why Madagascar Section - Light Gray BG */}
      <section className="py-12 sm:py-16 md:py-24 bg-tp-bg" id="why-madagascar" data-testid="why-madagascar-section">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-tp-text mb-2 sm:mb-4">
              Pourquoi Madagascar ?
            </h2>
            <p className="text-sm sm:text-base text-tp-text-light max-w-2xl mx-auto">
              Un vivier de talents francophones avec un excellent rapport qualité-coût
            </p>
          </div>
          
          <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <Card className="p-4 sm:p-6 bg-white border-tp-border hover:border-tp-primary-light/30 hover:shadow-lg transition-all" data-testid="why-card-francophone">
              <MessageSquare className="h-8 w-8 sm:h-10 sm:w-10 text-tp-accent mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-display font-semibold text-tp-text mb-1 sm:mb-2">Francophonie native</h3>
              <p className="text-xs sm:text-sm text-tp-text-light leading-relaxed">
                Le français est langue officielle. Communication fluide et sans barrière.
              </p>
            </Card>
            
            <Card className="p-4 sm:p-6 bg-white border-tp-border hover:border-tp-primary-light/30 hover:shadow-lg transition-all" data-testid="why-card-formation">
              <Users className="h-8 w-8 sm:h-10 sm:w-10 text-tp-accent mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-display font-semibold text-tp-text mb-1 sm:mb-2">Formation solide</h3>
              <p className="text-xs sm:text-sm text-tp-text-light leading-relaxed">
                Système éducatif francophone reconnu, diplômés qualifiés.
              </p>
            </Card>
            
            <Card className="p-4 sm:p-6 bg-white border-tp-border hover:border-tp-primary-light/30 hover:shadow-lg transition-all" data-testid="why-card-cost">
              <BarChart3 className="h-8 w-8 sm:h-10 sm:w-10 text-tp-accent mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-display font-semibold text-tp-text mb-1 sm:mb-2">Coûts optimisés</h3>
              <p className="text-xs sm:text-sm text-tp-text-light leading-relaxed">
                Rapport qualité-prix imbattable pour votre entreprise.
              </p>
            </Card>
            
            <Card className="p-4 sm:p-6 bg-white border-tp-border hover:border-tp-primary-light/30 hover:shadow-lg transition-all" data-testid="why-card-timezone">
              <Clock className="h-8 w-8 sm:h-10 sm:w-10 text-tp-accent mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-display font-semibold text-tp-text mb-1 sm:mb-2">Fuseau aligné</h3>
              <p className="text-xs sm:text-sm text-tp-text-light leading-relaxed">
                GMT+3, proche de l'Europe. Collaboration en temps réel.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Selection Method Section - White */}
      <section className="py-20 md:py-24 bg-white" id="selection" data-testid="selection-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-display font-semibold text-tp-text mb-6">
                Notre méthode de sélection
              </h2>
              <p className="text-tp-text-light mb-8 leading-relaxed">
                Chaque candidat passe par un processus rigoureux avant d'intégrer notre vivier. 
                Nous garantissons des profils fiables, compétents et immédiatement opérationnels.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-tp-accent/10 flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-tp-accent" />
                  </div>
                  <div>
                    <h4 className="font-medium text-tp-text mb-1">Vérification des compétences</h4>
                    <p className="text-sm text-tp-text-light">Tests techniques et mise en situation professionnelle</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-tp-accent/10 flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-tp-accent" />
                  </div>
                  <div>
                    <h4 className="font-medium text-tp-text mb-1">Évaluation du français</h4>
                    <p className="text-sm text-tp-text-light">Entretien oral et écrit pour valider la maîtrise linguistique</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-tp-accent/10 flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-tp-accent" />
                  </div>
                  <div>
                    <h4 className="font-medium text-tp-text mb-1">Analyse comportementale</h4>
                    <p className="text-sm text-tp-text-light">Évaluation du professionnalisme et de la fiabilité</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-tp-accent/10 flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-tp-accent" />
                  </div>
                  <div>
                    <h4 className="font-medium text-tp-text mb-1">Vérification des références</h4>
                    <p className="text-sm text-tp-text-light">Contact des anciens employeurs et validation du parcours</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-tp-bg rounded-2xl p-8 border border-tp-border">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="h-8 w-8 text-tp-primary" />
                <h3 className="text-xl font-display font-semibold text-tp-text">Garantie qualité</h3>
              </div>
              <p className="text-tp-text-light mb-6 leading-relaxed">
                Si le profil proposé ne correspond pas à vos attentes, nous vous présentons une alternative sans frais supplémentaires.
              </p>
              <div className="flex items-center gap-2 text-sm text-tp-accent font-medium">
                <CheckCircle className="h-4 w-4" />
                Période d'essai accompagnée
              </div>
            </div>
          </div>
        </div>
      </section>

      <ProcessSection steps={processSteps} onContactClick={scrollToContact} />

      <TestimonialsSection testimonials={testimonials} />

      <FAQSection faqItems={faqItems} onContactClick={scrollToContact} />

      {/* CTA / Contact Form Section - Primary Blue */}
      <section 
        className="py-12 sm:py-16 md:py-24 bg-tp-primary" 
        id="contact" 
        data-testid="contact-section"
      >
        <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-semibold text-white mb-2 sm:mb-4">
            Prêt à renforcer votre équipe ?
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-white/80 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Décrivez votre besoin et recevez une sélection de profils qualifiés sous 72h.
          </p>
          
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 max-w-xl mx-auto shadow-xl">
            <form onSubmit={handleFormSubmit} className="space-y-3 sm:space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-1.5 text-left">Nom complet *</Label>
                  <Input 
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Jean Dupont"
                    className="text-sm sm:text-base h-9 sm:h-10"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <Label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-1.5 text-left">Entreprise *</Label>
                  <Input 
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Nom de votre entreprise"
                    className="text-sm sm:text-base h-9 sm:h-10"
                    data-testid="contact-input-company"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-1.5 text-left">Email *</Label>
                  <Input 
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="jean@entreprise.com"
                    className="text-sm sm:text-base h-9 sm:h-10"
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <Label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-1.5 text-left">Téléphone</Label>
                  <Input 
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+33 6 00 00 00 00"
                    className="text-sm sm:text-base h-9 sm:h-10"
                    data-testid="contact-input-phone"
                  />
                </div>
              </div>
              
              <div>
                <Label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-1.5 text-left">
                  Type(s) de profil recherché(s) *
                </Label>
                <p className="text-[11px] sm:text-xs text-tp-text-light mb-2 text-left">
                  Sélectionnez un ou plusieurs profils (ex. Community Manager + Media Buyer).
                </p>
                <div
                  className="flex flex-wrap gap-2"
                  role="group"
                  aria-label="Types de profils recherchés"
                  data-testid="contact-talent-types-group"
                >
                  {talentTypes.map((type) => {
                    const selected = formData.talent_types.includes(type);
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleTalentType(type)}
                        aria-pressed={selected}
                        data-testid={`contact-talent-chip-${type.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                        className={
                          `text-xs sm:text-sm px-3 py-1.5 sm:py-2 rounded-full border transition-all select-none ` +
                          (selected
                            ? 'bg-tp-accent text-white border-tp-accent shadow-sm'
                            : 'bg-white text-tp-text border-gray-300 hover:border-tp-accent hover:text-tp-accent')
                        }
                      >
                        {selected && <span className="mr-1" aria-hidden="true">✓</span>}
                        {type}
                      </button>
                    );
                  })}
                </div>
                {formData.talent_types.length > 0 && (
                  <p className="text-[11px] sm:text-xs text-tp-text-light mt-2 text-left" data-testid="contact-talent-types-count">
                    {formData.talent_types.length} profil{formData.talent_types.length > 1 ? 's' : ''} sélectionné{formData.talent_types.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>
              
              <div>
                <Label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-1.5 text-left">Décrivez votre besoin</Label>
                <Textarea 
                  rows={3}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Ex: Je recherche un assistant virtuel bilingue..."
                  className="placeholder:text-gray-400 text-sm sm:text-base"
                  data-testid="contact-input-message"
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={formLoading}
                className="w-full bg-tp-accent hover:bg-tp-accent-light text-white text-sm sm:text-base h-10 sm:h-12"
                data-testid="contact-submit-button"
              >
                {formLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  'Recevoir des profils'
                )}
              </Button>
              
              <p className="text-[10px] sm:text-xs text-tp-text-light text-center">
                Réponse garantie sous 24h ouvrées
              </p>
            </form>
            
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-tp-border text-center">
              <p className="text-xs sm:text-sm text-tp-text-light mb-2 sm:mb-3">
                Vous êtes candidat et cherchez une opportunité ?
              </p>
              <Link to="/postuler">
                <Button variant="outline" className="gap-2 border-tp-primary text-tp-primary hover:bg-tp-primary hover:text-white transition-colors text-xs sm:text-sm h-8 sm:h-10" data-testid="contact-apply-link">
                  <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  Déposer ma candidature
                </Button>
              </Link>
              <p className="text-[10px] sm:text-xs text-tp-text-light mt-3">
                Nous vous recontacterons dans les plus brefs délais.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Dark */}
      <footer className="bg-tp-primary text-white py-8 sm:py-12" data-testid="client-footer">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
          {/* Postuler CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pb-6 sm:pb-8 mb-6 sm:mb-8 border-b border-white/15">
            <div>
              <p className="text-sm sm:text-base font-medium text-white">Vous êtes candidat ?</p>
              <p className="text-xs sm:text-sm text-white/60">Rejoignez notre réseau de talents francophones</p>
            </div>
            <Link to="/postuler">
              <Button 
                className="bg-tp-accent hover:bg-tp-accent-light text-white text-sm px-6 h-10"
                data-testid="footer-postuler-btn"
              >
                Postuler maintenant <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 sm:gap-6">
            <div>
              {logoDarkUrl ? (
                <img 
                  src={resolveAssetUrl(logoDarkUrl)}
                  alt={platformName}
                  className="h-12 sm:h-14 max-w-[200px] sm:max-w-[240px] object-contain"
                  data-testid="footer-logo"
                />
              ) : logoUrl ? (
                <img 
                  src={resolveAssetUrl(logoUrl)}
                  alt={platformName}
                  className="h-10 sm:h-12 max-w-[180px] sm:max-w-[220px] object-contain brightness-0 invert"
                  data-testid="footer-logo"
                />
              ) : (
                <span className="text-base sm:text-xl font-display font-semibold">
                  {platformName}
                </span>
              )}
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 md:gap-6 text-xs sm:text-sm text-white/60">
              <Link to="/postuler" className="hover:text-white transition-colors" data-testid="footer-link-postuler">
                Candidats
              </Link>
              <Link to="/cgv" className="hover:text-white transition-colors" data-testid="footer-link-cgv">
                CGV
              </Link>
              <Link to="/mentions-legales" className="hover:text-white transition-colors" data-testid="footer-link-mentions">
                Mentions légales
              </Link>
              <span className="hidden sm:inline text-white/30">|</span>
              <span>&copy; {new Date().getFullYear()} {platformName}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientHomePage;
